import apiEndPoints from '../../../routeConfig';
import { ApiService } from '../../../../service/api.service';
import { attempt, resultMessage } from '../../../../service/apiResult';

/**
 * Heavy-rain thresholds, one set per country.
 *
 * The eK Score Distress Index turns a day of very heavy rain on a farmer's plot (NASA POWER daily
 * rainfall) into an ENVIRONMENTAL distress event. What counts as "very heavy" is a weather agency's
 * own daily rainfall category, so it is set per country: India uses IMD's (very heavy at 115.6 mm,
 * extremely heavy at 204.5 mm). A country with no thresholds gets no heavy-rain events at all — its
 * farmers' environmental distress is shown as not observed, never as calm.
 */

const apiService = new ApiService();

const body = (result) => result?.data?.data ?? result?.data ?? null;
const ok = (data) => ({ success: true, data });
const fail = (message) => ({ success: false, error: { message } });

/** India Meteorological Department's daily rainfall categories, offered as a starting point. */
export const IMD_DEFAULTS = {
  veryHeavyMm: 115.6,
  extremelyHeavyMm: 204.5,
  extremelyHeavySeverity: 8.5,
  source: 'IMD',
  notes: 'India Meteorological Department daily rainfall categories: very heavy 115.6-204.4 mm, extremely heavy >= 204.5 mm.',
};

/** The severity a very-heavy spell gets: the event type's own default (AREA_HEAVY_RAIN), not configured here. */
export const VERY_HEAVY_SEVERITY = 4.5;

/** The most heavy rain can add to a farmer's distress on its own (the engine's AREA_CAP). */
export const AREA_CAP = 5.0;

const fromApi = (row) => ({
  id: row.id,
  countryId: row.countryId,
  countryName: row.countryName,
  veryHeavyMm: row.veryHeavyMm,
  extremelyHeavyMm: row.extremelyHeavyMm,
  extremelyHeavySeverity: row.extremelyHeavySeverity,
  source: row.source || '',
  notes: row.notes || '',
  updatedAt: row.updatedAt || null,
});

const toApi = (draft) => ({
  veryHeavyMm: Number(draft.veryHeavyMm),
  extremelyHeavyMm: Number(draft.extremelyHeavyMm),
  extremelyHeavySeverity: Number(draft.extremelyHeavySeverity),
  source: String(draft.source || '').trim(),
  notes: draft.notes ? String(draft.notes) : null,
});

const isNumber = (value) => value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value));

/** The same rules the API and the table enforce, so a bad value is caught in the form. */
export function validateThresholds(draft) {
  const issues = {};
  const veryHeavy = Number(draft.veryHeavyMm);
  const extremelyHeavy = Number(draft.extremelyHeavyMm);
  const severity = Number(draft.extremelyHeavySeverity);

  if (!isNumber(draft.veryHeavyMm) || veryHeavy <= 0) {
    issues.veryHeavyMm = 'Enter the daily rainfall, in mm, above 0.';
  } else if (veryHeavy > 2000) {
    issues.veryHeavyMm = 'That is above any recorded daily rainfall.';
  }

  if (!isNumber(draft.extremelyHeavyMm) || extremelyHeavy <= 0) {
    issues.extremelyHeavyMm = 'Enter the daily rainfall, in mm, above 0.';
  } else if (extremelyHeavy > 2000) {
    issues.extremelyHeavyMm = 'That is above any recorded daily rainfall.';
  } else if (!issues.veryHeavyMm && extremelyHeavy < veryHeavy) {
    issues.extremelyHeavyMm = 'Must not be below the very heavy threshold.';
  }

  if (!isNumber(draft.extremelyHeavySeverity) || severity < 0 || severity > 10) {
    issues.extremelyHeavySeverity = 'Enter a severity from 0 to 10.';
  }

  const source = String(draft.source || '').trim();
  if (!source) {
    issues.source = 'Name the weather agency these categories come from.';
  } else if (source.length > 100) {
    issues.source = 'Keep it to 100 characters or fewer.';
  }

  return issues;
}

/* ---------------------------------------------------------------- storage */

/** Every country's thresholds, keyed by country id. */
export async function getAllThresholds() {
  const result = await attempt(() => apiService.get(apiEndPoints.heavyRainThresholds));
  if (!result.success) return fail(resultMessage(result, 'Could not load the heavy-rain thresholds.'));

  const byCountry = {};
  (body(result) || []).map(fromApi).forEach((row) => {
    byCountry[row.countryId] = row;
  });
  return ok(byCountry);
}

export async function saveThresholds(countryId, draft) {
  if (!countryId) return fail('Choose a country first.');

  const issues = validateThresholds(draft);
  const first = Object.values(issues)[0];
  if (first) return fail(first);

  const result = await attempt(() =>
    apiService.update(`${apiEndPoints.heavyRainThresholds}/${countryId}`, toApi(draft)));
  if (!result.success) return fail(resultMessage(result, 'Could not save the thresholds.'));

  return ok(fromApi(body(result)));
}

export async function removeThresholds(countryId) {
  const result = await attempt(() => apiService.delete(`${apiEndPoints.heavyRainThresholds}/${countryId}`));
  if (!result.success) return fail(resultMessage(result, 'Could not remove the thresholds.'));

  return ok(null);
}
